import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { api } from '../lib/api'

export default function Setup2FAPage() {
  const navigate = useNavigate()
  const [step, setStep] = useState<'loading' | 'setup' | 'verify'>('loading')
  const [qrCode, setQrCode] = useState('')
  const [secret, setSecret] = useState('')
  const [code, setCode] = useState('')
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    initSetup()
  }, [])

  const initSetup = async () => {
    try {
      const { data } = await api.post('/fapi/totp/setup')
      setQrCode(data.qr_code)
      setSecret(data.secret)
      setStep('setup')
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Erreur lors de l\'initialisation')
      setStep('setup')
    }
  }

  const verifyAndEnable = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setLoading(true)
    try {
      const { data } = await api.post('/fapi/totp/enable', { code })
      setRecoveryCodes(data.recovery_codes)
      setStep('verify')
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Code invalide')
    } finally {
      setLoading(false)
    }
  }

  const downloadRecoveryCodes = () => {
    const text = recoveryCodes.join('\n')
    const blob = new Blob([text], { type: 'text/plain' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'totemium-recovery-codes.txt'
    a.click()
    URL.revokeObjectURL(url)
  }

  if (step === 'loading') {
    return (
      <div className="mx-auto flex w-full max-w-2xl flex-col gap-10 px-4 py-12 sm:py-16">
        <div className="text-center">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-emerald-500 border-r-transparent"></div>
          <p className="mt-4 text-sm text-zinc-600 dark:text-zinc-400">Préparation de la 2FA...</p>
        </div>
      </div>
    )
  }

  if (step === 'verify') {
    return (
      <div className="mx-auto flex w-full max-w-2xl flex-col gap-10 px-4 py-12 sm:py-16">
        <div className="relative overflow-hidden rounded-3xl border border-emerald-100/80 bg-white/90 shadow-2xl shadow-emerald-500/10 backdrop-blur dark:border-emerald-900/40 dark:bg-zinc-950/90 dark:shadow-none">
          <div aria-hidden className="absolute inset-0 -z-10 bg-gradient-to-br from-emerald-500/15 via-emerald-400/10 to-transparent" />
          <div className="relative px-8 py-12 sm:px-12">
            <div className="space-y-6">
              <div className="text-center">
                <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-emerald-100 dark:bg-emerald-900/50">
                  <svg className="h-8 w-8 text-emerald-600 dark:text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                </div>
                <h2 className="text-2xl font-bold text-zinc-900 dark:text-white">2FA activée !</h2>
                <p className="mt-2 text-sm text-zinc-600 dark:text-zinc-400">
                  Votre compte est maintenant protégé par l'authentification à deux facteurs.
                </p>
              </div>

              <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 dark:border-amber-900/50 dark:bg-amber-950/40">
                <div className="flex items-start gap-3">
                  <svg className="h-5 w-5 flex-shrink-0 text-amber-600 dark:text-amber-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  <div className="flex-1">
                    <h3 className="font-semibold text-amber-900 dark:text-amber-200">Codes de récupération</h3>
                    <p className="mt-1 text-sm text-amber-800 dark:text-amber-300">
                      Sauvegardez ces codes en lieu sûr. Ils vous permettront d'accéder à votre compte si vous perdez votre appareil.
                    </p>
                  </div>
                </div>
              </div>

              <div className="rounded-xl border border-zinc-200 bg-zinc-50 p-6 dark:border-zinc-800 dark:bg-zinc-900/50">
                <div className="mb-4 flex items-center justify-between">
                  <h3 className="font-semibold text-zinc-900 dark:text-white">Vos codes de récupération</h3>
                  <button
                    onClick={downloadRecoveryCodes}
                    className="inline-flex items-center gap-2 rounded-lg border border-emerald-200 bg-white px-3 py-1 text-xs font-medium text-emerald-700 shadow-sm transition hover:bg-emerald-50 dark:border-emerald-800 dark:bg-emerald-900/50 dark:text-emerald-200 dark:hover:bg-emerald-900/70"
                  >
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    </svg>
                    Télécharger
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-2 font-mono text-sm">
                  {recoveryCodes.map((code, i) => (
                    <div key={i} className="rounded-lg bg-white px-3 py-2 text-zinc-900 dark:bg-zinc-950 dark:text-zinc-100">
                      {code}
                    </div>
                  ))}
                </div>
              </div>

              <button
                onClick={() => navigate('/profile')}
                className="w-full rounded-full bg-emerald-500 px-5 py-3 text-sm font-semibold text-white shadow-lg shadow-emerald-500/40 transition hover:bg-emerald-600"
              >
                Terminer
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="mx-auto flex w-full max-w-2xl flex-col gap-10 px-4 py-12 sm:py-16">
      <div className="relative overflow-hidden rounded-3xl border border-emerald-100/80 bg-white/90 shadow-2xl shadow-emerald-500/10 backdrop-blur dark:border-emerald-900/40 dark:bg-zinc-950/90 dark:shadow-none">
        <div aria-hidden className="absolute inset-0 -z-10 bg-gradient-to-br from-emerald-500/15 via-emerald-400/10 to-transparent" />
        <div className="relative px-8 py-12 sm:px-12">
          <div className="space-y-6">
            <div className="text-center">
              <span className="inline-flex items-center gap-2 rounded-full border border-emerald-200/70 bg-white/80 px-4 py-1 text-xs font-semibold uppercase tracking-[0.25em] text-emerald-600 shadow-sm dark:border-emerald-900/50 dark:bg-emerald-900/40 dark:text-emerald-300">
                Totemium
              </span>
              <h1 className="mt-4 text-2xl font-bold text-zinc-900 dark:text-white sm:text-3xl">
                Activer l'authentification à deux facteurs
              </h1>
              <p className="mt-2 text-sm text-zinc-600 dark:text-zinc-400">
                Sécurisez votre compte avec un code à usage unique.
              </p>
            </div>

            <div className="space-y-4">
              <div className="rounded-xl border border-zinc-200 bg-zinc-50 p-6 dark:border-zinc-800 dark:bg-zinc-900/50">
                <h3 className="mb-4 font-semibold text-zinc-900 dark:text-white">Étape 1 : Scanner le QR code</h3>
                <p className="mb-4 text-sm text-zinc-600 dark:text-zinc-400">
                  Utilisez une application d'authentification (Google Authenticator, Authy, 1Password, etc.) pour scanner ce code :
                </p>
                {qrCode ? (
                  <div className="flex justify-center">
                    <img src={qrCode} alt="QR Code 2FA" className="h-64 w-64 rounded-lg bg-white p-4" />
                  </div>
                ) : (
                  <div className="flex h-64 items-center justify-center rounded-lg bg-white">
                    <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-emerald-500 border-r-transparent"></div>
                  </div>
                )}
                <details className="mt-4">
                  <summary className="cursor-pointer text-xs text-zinc-500 hover:text-zinc-700 dark:text-zinc-400 dark:hover:text-zinc-200">
                    Impossible de scanner ? Saisissez manuellement
                  </summary>
                  <div className="mt-2 rounded-lg bg-white p-3 font-mono text-xs text-zinc-900 dark:bg-zinc-950 dark:text-zinc-100">
                    {secret}
                  </div>
                </details>
              </div>

              <div className="rounded-xl border border-zinc-200 bg-zinc-50 p-6 dark:border-zinc-800 dark:bg-zinc-900/50">
                <h3 className="mb-4 font-semibold text-zinc-900 dark:text-white">Étape 2 : Vérifier le code</h3>
                <form onSubmit={verifyAndEnable} className="space-y-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-zinc-600 dark:text-zinc-300" htmlFor="code">
                      Code à 6 chiffres
                    </label>
                    <input
                      id="code"
                      className="w-full rounded-xl border border-emerald-100/80 bg-white/90 px-4 py-2 text-center text-lg font-mono tracking-widest text-zinc-900 shadow-sm transition focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-200 dark:border-emerald-900/50 dark:bg-zinc-950/80 dark:text-zinc-100 dark:focus:border-emerald-500 dark:focus:ring-emerald-900/60"
                      type="text"
                      value={code}
                      onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      placeholder="000000"
                      required
                      maxLength={6}
                    />
                  </div>
                  {error && (
                    <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900/60 dark:bg-red-950/40 dark:text-red-200">
                      {error}
                    </div>
                  )}
                  <button
                    disabled={loading || code.length !== 6}
                    className="w-full rounded-full bg-emerald-500 px-5 py-3 text-sm font-semibold text-white shadow-lg shadow-emerald-500/40 transition hover:bg-emerald-600 disabled:cursor-not-allowed disabled:opacity-70"
                  >
                    {loading ? 'Vérification…' : 'Activer la 2FA'}
                  </button>
                </form>
              </div>
            </div>

            <div className="text-center">
              <button
                onClick={() => navigate('/profile')}
                className="text-sm text-zinc-500 hover:underline dark:text-zinc-400"
              >
                ← Annuler
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
